import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { findPathToKey } from '../../helpers/App';

import DownloadReasonsElement from './ReasonsElement';
import ErrorBoundary from '../ErrorBoundary';

const DownloadReasonSelector = ({
  reasons,
  answerTree,
  handleAnswerTree,
  showAnswerTreeErrors,
}) => {
  const getAnswerTree = useCallback((depth, path, element) => {
    if (depth === 0) {
      return Object.assign({}, ...element.map((e, i) => ({
        [i]: {
          ...(e.type === 'question' && { question: e.label }),
          ...(e.required && { required: true, requiredError: true }),
          answers: [],
          nodes: Array.isArray(e.nodes)
            ? _.compact(e.nodes.map((n, j) => getAnswerTree(1, [i, j], n)))
            : [],
        },
      })));
    }

    if (element.nodes.length > 0) {
      return {
        [path.join('_')]: {
          ...(element.type === 'question' && { question: element.label }),
          ...(element.required && { required: true, requiredError: true }),
          answers: [],
          nodes: Array.isArray(element.nodes)
            ? _.compact(element.nodes.map((n, i) => getAnswerTree(depth + 1, [...path, i], n)))
            : [],
        },
      };
    }

    return null;
  }, []);

  const handleChange = (
    answerPath,
    answerValue,
    answerType,
    answerChecked = false,
    label = null,
  ) => {
    const clearRequiredErrors = (node) => {
      const nodeCopy = node;
      if (nodeCopy.requiredError) {
        delete nodeCopy.requiredError;
      }

      if (nodeCopy.nodes && Array.isArray(nodeCopy.nodes)) {
        nodeCopy.nodes.forEach(childNodeWrapper => {
          const childKey = Object.keys(childNodeWrapper)[0];
          const childNode = childNodeWrapper[childKey];
          clearRequiredErrors(childNode);
        });
      }
    };

    const processNode = (node, parentAnswers) => {
      const nodeCopy = node;

      if (nodeCopy.question && nodeCopy.required) {
        const hasAnswers = nodeCopy.answers && nodeCopy.answers.length > 0;
        const isInActiveBranch = parentAnswers === null || parentAnswers.length === 0;

        if (nodeCopy.required && !hasAnswers && isInActiveBranch) {
          nodeCopy.requiredError = true;
        } else {
          delete nodeCopy.requiredError;
        }
      }

      const currentAnswers = nodeCopy.answers || [];

      if (nodeCopy.nodes && Array.isArray(nodeCopy.nodes)) {
        nodeCopy.nodes.forEach(childNodeWrapper => {
          const childKey = Object.keys(childNodeWrapper)[0];
          const childNode = childNodeWrapper[childKey];

          const isActive = currentAnswers.length === 0 ||
                           currentAnswers.some(ans => childKey.startsWith(ans.id));

          if (isActive) {
            processNode(childNode, currentAnswers);
          } else {
            clearRequiredErrors(childNode);
          }
        });
      }
    };

    const updateRequiredErrors = (tree) => {
      const topLevelKeys = Object.keys(tree);

      topLevelKeys.forEach(key => {
        const node = tree[key];
        processNode(node, null);
      });
    };

    const updateNode = (tree, path, identifier, answer, type, checked) => {
      const newTree = _.cloneDeep(tree);

      const pathToKey = findPathToKey(newTree, path);

      let newAnswers = _.get(newTree, pathToKey)?.answers || [];

      if (type === 'checkbox') {
        if (checked && !newAnswers.some(a => a.id === identifier)) {
          newAnswers.push({ id: identifier, value: answer });
        }
        if (!checked) {
          newAnswers = newAnswers.filter(a => identifier !== a.id);
        }
      }

      if (type === 'select') {
        if (answer === null) {
          newAnswers = [];
        } else {
          newAnswers = [{ id: identifier, value: answer }];
        }
      }

      if (type === 'radio') {
        newAnswers = [{ id: identifier, value: answer }];
      }

      if (type === 'text') {
        newAnswers = answer ? [
          ...newAnswers.filter(a => identifier !== a.id),
          { id: identifier, label, value: answer },
        ] : [];
      }

      _.set(newTree, `${pathToKey}.answers`, newAnswers);

      updateRequiredErrors(newTree);

      return newTree;
    };

    const elementPath = answerPath.split('_').slice(0, -1).join('_');

    handleAnswerTree(updateNode(
      answerTree,
      elementPath,
      answerPath,
      answerValue,
      answerType,
      answerChecked,
    ));
  };

  useEffect(() => {
    if (Object.keys(answerTree).length === 0) {
      handleAnswerTree(getAnswerTree(0, [0], reasons));
    }
  }, [getAnswerTree, reasons, handleAnswerTree, answerTree]);

  return (
    <ErrorBoundary>
      {reasons.map((r, i) => (
        <DownloadReasonsElement
          key={[i].join('_')}
          element={r}
          depth={0}
          path={[i]}
          handleChange={handleChange}
          answerTree={answerTree}
          showAnswerTreeErrors={showAnswerTreeErrors}
        />
      ))}
    </ErrorBoundary>
  );
};

DownloadReasonSelector.propTypes = {
  reasons: PropTypes.array.isRequired,
  answerTree: PropTypes.object.isRequired,
  handleAnswerTree: PropTypes.func.isRequired,
  showAnswerTreeErrors: PropTypes.bool.isRequired,
};

export default DownloadReasonSelector;
